import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Mic, MicOff, Send, X, Sparkles, Volume2, VolumeX, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, inr } from "@/lib/api";

const QUICK = ["Show biryani near me", "Best rated restaurants", "Show offers", "Track my order", "Naaku pizza kavali"];

export default function VSAI() {
  const nav = useNavigate();
  const loc = useLocation();
  const { cart, addItem, clear } = useCart();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ from: "ai", text: "Hi! I'm VS AI. Speak or type to order — English or Telugu. Try 'Naaku chicken biryani kavali'." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [pendingCart, setPendingCart] = useState(null);
  const recRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const speak = (t) => {
    if (!voiceOn || !window.speechSynthesis) return;
    try { window.speechSynthesis.cancel(); const u = new SpeechSynthesisUtterance(t); u.rate = 0.98; window.speechSynthesis.speak(u); } catch {}
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("Voice input isn't supported in this browser. Please type.");
    const rec = new SR();
    rec.lang = "en-IN"; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onerror = () => { setListening(false); toast.error("Couldn't hear you. Try again."); };
    rec.onresult = (e) => { const t = e.results[0][0].transcript; setInput(t); send(t); };
    recRef.current = rec; rec.start();
  };

  const addResolved = (resolved, replace) => {
    if (replace) clear();
    resolved.forEach(({ restaurant, item, qty }) => {
      const unit = Math.round(item.price * (1 - (item.discount_pct || 0) / 100));
      addItem(restaurant, { menu_item_id: item.id, name: item.name, image: item.image, veg: item.veg, qty: qty || 1, variant: null, addons: [], spice: null, instructions: "", unit_total: unit });
    });
    const names = resolved.map((r) => `${r.qty || 1}× ${r.item.name}`).join(", ");
    push("ai", `Added ${names} to your cart. Ready to view cart or checkout?`, { chips: [["View Cart", "/cart"], ["Checkout", "/checkout"]] });
    speak(`Added ${names} to your cart.`);
    setPendingCart(null);
  };

  const handleAction = (r) => {
    const { action, data } = r;
    if (action === "navigate" && r.params?.route) nav(r.params.route);
    else if (action === "view_cart") nav("/cart");
    else if (action === "checkout") nav(cart ? "/checkout" : "/");
    else if (action === "show_offers") nav("/offers");
    else if (action === "open_restaurant" && data.open_restaurant_id) nav(`/restaurant/${data.open_restaurant_id}`);
    else if (action === "reorder" && data.open_restaurant_id) nav(`/restaurant/${data.open_restaurant_id}`);
    else if (action === "track_order" && data.order_id) nav(`/order/${data.order_id}`);
    else if (action === "add_to_cart" && data.resolved?.length) {
      const target = data.resolved[0].restaurant;
      if (cart && cart.restaurant.id !== target.id) setPendingCart(data.resolved);
      else addResolved(data.resolved, false);
    }
  };

  const push = (from, text, extra = {}) => setMsgs((m) => [...m, { from, text, ...extra }]);

  const send = async (override) => {
    const text = (override ?? input).trim();
    if (!text || busy) return;
    push("user", text); setInput(""); setBusy(true);
    try {
      const cartSummary = cart ? `${cart.items.length} items from ${cart.restaurant.name}` : "empty";
      const { data: r } = await api.post("/ai/chat", { message: text, context: { route: loc.pathname, cart_summary: cartSummary } });
      const extra = {};
      if (r.data?.restaurants?.length) extra.restaurants = r.data.restaurants;
      if (r.data?.dishes?.length) extra.dishes = r.data.dishes;
      push("ai", r.reply, extra);
      speak(r.reply);
      handleAction(r);
    } catch (e) {
      push("ai", "Sorry, I had trouble with that. You can say 'Show biryani near me' or 'Track my order'.");
    } finally { setBusy(false); }
  };

  if (loc.pathname === "/login") return null;

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-[140px] right-4 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-coral to-coral-dark text-white shadow-xl flex items-center justify-center tap" data-testid="vsai-fab" aria-label="Open VS AI">
          <Sparkles size={24} />
        </button>
      )}
      {open && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-[520px] rounded-t-3xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="vsai-panel">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-gradient-to-r from-coral to-coral-dark text-white rounded-t-3xl">
              <Sparkles size={20} /><div className="flex-1"><div className="font-display font-bold">VS AI</div><div className="text-[11px] text-white/85">Voice ordering · English & Telugu</div></div>
              <button onClick={() => setVoiceOn((v) => !v)} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center" data-testid="vsai-voice-toggle">{voiceOn ? <Volume2 size={17} /> : <VolumeX size={17} />}</button>
              <button onClick={() => setOpen(false)} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${m.from === "user" ? "bg-coral text-white" : "bg-surface text-ink"} rounded-2xl px-3.5 py-2.5`}>
                    <div className="text-sm">{m.text}</div>
                    {m.restaurants?.length > 0 && (
                      <div className="flex gap-2 overflow-x-auto hide-scrollbar mt-2 -mx-1 px-1">
                        {m.restaurants.map((r) => (
                          <button key={r.id} onClick={() => { setOpen(false); nav(`/restaurant/${r.id}`); }} className="min-w-[130px] w-[130px] bg-white rounded-xl border border-border p-1.5 text-left" data-testid={`vsai-rest-${r.id}`}>
                            <img src={r.image} alt="" className="w-full h-16 object-cover rounded-lg" /><div className="text-[11px] font-bold truncate mt-1 text-ink">{r.name}</div><div className="text-[10px] text-ink-600">{r.rating}★ · {r.delivery_time_min}m</div>
                          </button>
                        ))}
                      </div>
                    )}
                    {m.dishes?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {m.dishes.slice(0, 4).map((d) => (
                          <button key={d.id} onClick={() => { setOpen(false); nav(`/restaurant/${d.restaurant_id}`); }} className="w-full flex items-center gap-2 bg-white rounded-lg border border-border p-1.5 text-left" data-testid={`vsai-dish-${d.id}`}>
                            <img src={d.image} alt="" className="w-9 h-9 rounded object-cover" /><div className="min-w-0"><div className="text-[11px] font-bold truncate text-ink">{d.name}</div><div className="text-[10px] text-ink-600 truncate">{d.restaurant_name} · {inr(d.price)}</div></div>
                          </button>
                        ))}
                      </div>
                    )}
                    {m.chips?.length > 0 && (
                      <div className="flex gap-2 mt-2">{m.chips.map(([l, to]) => <button key={to} onClick={() => { setOpen(false); nav(to); }} className="bg-coral text-white text-xs font-bold px-3 py-1.5 rounded-lg">{l}</button>)}</div>
                    )}
                  </div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="bg-surface rounded-2xl px-3.5 py-2.5"><Loader2 className="animate-spin text-coral" size={16} /></div></div>}
              {pendingCart && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3">
                  <div className="text-sm font-semibold text-ink">Your cart has items from {cart?.restaurant.name}. Replace with {pendingCart[0].restaurant.name}?</div>
                  <div className="flex gap-2 mt-2"><button onClick={() => addResolved(pendingCart, true)} className="bg-coral text-white text-xs font-bold px-3 py-1.5 rounded-lg" data-testid="vsai-replace">Replace Cart</button><button onClick={() => setPendingCart(null)} className="bg-white border border-border text-xs font-bold px-3 py-1.5 rounded-lg">Keep Existing</button></div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            <div className="px-3 pt-2 pb-1 flex gap-2 overflow-x-auto hide-scrollbar border-t border-border">
              {QUICK.map((q) => <button key={q} onClick={() => send(q)} className="bg-coral-light text-coral text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap tap">{q}</button>)}
            </div>
            <div className="p-3 flex items-center gap-2">
              <button onClick={listening ? () => recRef.current?.stop() : startVoice} className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 tap ${listening ? "bg-bad text-white animate-pulse" : "bg-coral text-white"}`} data-testid="vsai-mic">{listening ? <MicOff size={20} /> : <Mic size={20} />}</button>
              <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Speak or type your order…" className="flex-1 min-w-0 bg-surface rounded-full px-4 py-3 text-sm outline-none focus:ring-2 ring-coral" data-testid="vsai-input" />
              <button onClick={() => send()} disabled={busy} className="w-11 h-11 rounded-full bg-ink text-white flex items-center justify-center shrink-0 tap" data-testid="vsai-send"><Send size={18} /></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
